import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePortfolioRebalanceStore } from "../Store/portfolioRebalanceStore";
import { useAuth } from "../Context/AuthProvider";
import OrderPreviewTable from "../Component/PortfolioRebalance/OrderPreviewTable";
import BondTable from "../Component/PortfolioRebalance/BondTable";
import BondDetailsModal from "../Component/BondDetails/BondDetailsModal";
import OrderConfirmationModal from "../Component/BondLadderComponents/OrderConfirmationModal";
import OrderSuccessModal from "../Component/BondLadderComponents/OrderSuccessModal";
import {
  fetchSecurityInfo,
  placeBulkOrdersPortfolioRebalance,
} from "../http/api";
import useSnackbar from "../Utils/useSnackbar";
import { usePortfolioRebalanceWebSocketUpdates } from "../Component/PortfolioRebalance/usePortfolioRebalanceWebSocketUpdates";
import EditQtyPortfolioRebalance from "../Component/PortfolioRebalance/EditQtyPortfolioRebalance";
import EditEndingWeightPortfolioRebalance from "../Component/PortfolioRebalance/EditEndingWeightPortfolioRebalance";
import InformativeModal from "../Component/PortfolioRebalance/InformativeModal";
import { clearConfigIfOutdated } from "../Utils/columnVersionManager";
import { stagingOrderFields } from "../Constants/stagingBlotter";
import { tokens, withAlpha } from '../Common/designTokens'; // added
import { updateTradeRowCalculations, validateCalculatedRow } from "../Utils/tradeCalculationHelpers";
import { usePageSubscription } from "../Utils/WebSocketProvider.jsx";

// Editable field configurations for cell renderers
const EDITABLE_FIELDS = [
  {
    key: "tradeQuantity",
    propName: "tradeQtyCellRenderer",
    formatValue: (value) => value !== undefined && value !== null && value !== ""
      ? Number(value).toLocaleString("en-US")
      : ""
  },
  {
    key: "endingQuantity",
    propName: "endingQtyCellRenderer",
    formatValue: (value) => value !== undefined && value !== null && value !== ""
      ? Number(value).toLocaleString("en-US")
      : ""
  },
  {
    key: "tradeValue",
    propName: "tradeValueCellRenderer",
    formatValue: (value) => value !== undefined && value !== null && value !== ""
      ? Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : ""
  },
  {
    key: "endingSecurityMarketValue",
    propName: "endingSecurityMarketValueCellRenderer",
    formatValue: (value) => value !== undefined && value !== null && value !== ""
      ? Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : ""
  },
  {
    key: "endingSecurityWeightPercentage",
    propName: "endingWeightCellRenderer",
    formatValue: (value) => value !== undefined && value !== null && value !== ""
      ? Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })
      : ""
  },
];

const PORTFOLIO_PLAY_COLUMNS = [
  { key: "bondType", name: "Bond Type", render: (value) => value || "---" },
  { key: "securityCharge", name: "Factored Securities", render: (value) => value || "---" },
  { key: "side", name: "Side" },
  { key: "cusip", name: "SecurityId" },
  { key: "securityDesc", name: "Security Description" },
  { key: "currentPrice", name: "Trading Price", numeric: true },
  { key: "currentQuantity", name: "Current Qty", numeric: true },
  { key: "securityWeightPercentage", name: "Current Weight %", numeric: true },
  { key: "securityMarketValue", name: "Current Market Value", numeric: true },
  { key: "endingQuantity", name: "Ending Qty", numeric: true },
  { key: "endingSecurityWeightPercentage", name: "Ending Weight %", numeric: true },
  { key: "endingSecurityMarketValue", name: "Ending Market Value", numeric: true },
  { key: "tradeQuantity", name: "Trade Qty", numeric: true },
  { key: "tradeValue", name: "Trade Value", numeric: true },
  { key: "accruedInterest", name: "Accrued Interest", numeric: true },
  { key: "maturityDate", name: "Maturity Date" },
  { key: "moodyRating", name: "Moody's Rating", render: (value) => value || "---" },
  { key: "spRating", name: "S&P Rating", render: (value) => value || "---" },
  { key: "couponPer", name: "Coupon", render: (value) => value ?? "---" },
  { key: "yield", name: "YTM", numeric: true },
  { key: "yieldToWorst", name: "YTW", numeric: true },
  { key: "dv01", name: "DV01", numeric: true },
  { key: "duration", name: "$ Duration", numeric: true },
];
const OrderPreviewScreen = () => {
  const location = useLocation();
  const isPortfolioPlayMode =
    location.pathname.includes("/portfolio-rebalance/portfolio-play") ||
    location.state?.mode === "portfolio-play";
  const initialPortfolioPlayTrades = React.useMemo(
    () => (Array.isArray(location.state?.initialTrades) ? location.state.initialTrades : []),
    [location.state?.initialTrades]
  );

  // Ensure column config is up-to-date for order preview
  React.useEffect(() => {
    clearConfigIfOutdated("order-preview", PORTFOLIO_PLAY_COLUMNS);
    clearConfigIfOutdated("portfolio-play", PORTFOLIO_PLAY_COLUMNS);
  }, []);

  const navigate = useNavigate();
  const { token } = useAuth();
  const { showError, SnackbarComponent } = useSnackbar();
  usePageSubscription("accountPositions");

  // Initialize WebSocket updates
  usePortfolioRebalanceWebSocketUpdates();

  const {
    portfolioSummary,
    accountSummary,
    selectedAccount,
    accounts,
    snpRatingOptions,
    moodyRatingOptions,
    rebalanceApiData,
    rebalanceApiLoading,
    rebalanceApiError,
    clearAchievedTargets,
  } = usePortfolioRebalanceStore();

  // Bond details modal state
  const [bondDetailsOpen, setBondDetailsOpen] = useState(false);
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);
  const [bondDetailsLoading, setBondDetailsLoading] = useState(false);

  // Modal states
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isOrderProcessing, setIsOrderProcessing] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isOrderSuccess, setIsOrderSuccess] = useState(false);

  // Trade states
  const [recommendedTrades, setRecommendedTrades] = useState([]);
  const [userEditedTrades, setUserEditedTrades] = useState([]);
  const [tradesRecalculated, setTradesRecalculated] = useState([]);

  // Track recalculate loading state
  const [isRecalculateLoading, setIsRecalculateLoading] = useState(false);

  // Portfolio play table local sort state
  const [playSortConfig, setPlaySortConfig] = useState({ key: null, direction: "asc" });

  // Edit Qty modal state
  const [editQtyOpen, setEditQtyOpen] = useState(false);
  // Remove index-based tracking; rely on row data to identify the trade uniquely
  // const [editQtyRowIndex, setEditQtyRowIndex] = useState(null);
  const [editQtyRowData, setEditQtyRowData] = useState(null);

  // Edit Ending Weight modal state
  const [editEndingWeightOpen, setEditEndingWeightOpen] = useState(false);
  const [editEndingWeightRowData, setEditEndingWeightRowData] = useState(null);

  // Informative modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { userId } = useAuth();

  const hasNonZeroQtyAndTradeValue = (trade) => {
    const qty = Number(trade?.tradeQuantity);
    const tradeValue = Number(trade?.tradeValue);

    if (!Number.isFinite(qty) || !Number.isFinite(tradeValue)) {
      return false;
    }

    return Math.abs(qty) > 0 && Math.abs(tradeValue) > 0;
  };

  const eligibleTradesForSubmit = React.useMemo(
    () =>
      userEditedTrades.filter((trade) => {
        const isIncluded = isPortfolioPlayMode ? true : trade.includeInRebalancing;
        return isIncluded && hasNonZeroQtyAndTradeValue(trade);
      }),
    [userEditedTrades, isPortfolioPlayMode]
  );
  useEffect(() => {
    //only if in portfolio play mode
    if (isPortfolioPlayMode) {
      clearAchievedTargets();
    }
  }, [isPortfolioPlayMode, clearAchievedTargets]);
  // Calculate preview metrics dynamically based on current userEditedTrades
  const previewMetrics = React.useMemo(() => {
    if (!userEditedTrades || userEditedTrades.length === 0) {
      return rebalanceApiData?.summary || {
        buyTradeValue: 0,
        buyTradeAmt: 0,
        sellTradeValue: 0,
        sellTradeAmt: 0,
        netTradeValue: 0,
        totalEndingSecurityMarketValue: 0,
        turnover: 0,
        totalEndingMarketValue: rebalanceApiData?.summary?.totalEndingMarketValue || 0,
      };
    }

    // For portfolio play, metrics should reflect non-zero trades even when
    // includeInRebalancing defaults to false.
    const activeTrades = userEditedTrades.filter((trade) => {
      const isIncluded = isPortfolioPlayMode ? true : trade.includeInRebalancing;
      return isIncluded && hasNonZeroQtyAndTradeValue(trade);
    });

    // Calculate buy and sell trades
    const buyTrades = activeTrades.filter(
      (t) => String(t.side || t.action).toUpperCase() === "BUY"
    );
    const sellTrades = activeTrades.filter(
      (t) => String(t.side || t.action).toUpperCase() === "SELL"
    );

    // Count of BUY/SELL trades
    const buyTradeValue = buyTrades.length;
    const sellTradeValue = sellTrades.length;

    // Sum of trade values for buy and sell
    const buyTradeAmt = buyTrades.reduce((sum, t) => sum + (t.tradeValue || 0), 0);
    const sellTradeAmt = sellTrades.reduce((sum, t) => sum + (t.tradeValue || 0), 0);

    // Net trade value is sum of all trade values
    const netTradeValue = activeTrades.reduce(
      (sum, t) => sum + (t.tradeValue || 0),
      0
    );

    // Calculate totalEndingSecurityMarketValue as sum of all ending market values from trades
    const totalEndingSecurityMarketValue = userEditedTrades.reduce(
      (sum, bond) => sum + (parseFloat(bond.endingSecurityMarketValue) || 0),
      0
    );

    // Calculate turnover percentage using netTradeValue / totalCurrValue from accountSummary
    const totalCurrValue = accountSummary.totalCurrValue || 0;
    const turnover = totalCurrValue > 0 ? (netTradeValue / totalCurrValue) : 0;

    return {
      buyTradeValue,
      buyTradeAmt,
      sellTradeValue,
      sellTradeAmt,
      netTradeValue,
      totalEndingSecurityMarketValue,
      turnover,
      totalEndingMarketValue: rebalanceApiData?.summary?.totalEndingMarketValue || 0,

    };
  }, [
    userEditedTrades,
    accountSummary.totalCurrValue,
    rebalanceApiData,
    isPortfolioPlayMode,
  ]);

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value === "number") {
      return `$${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return value || "---";
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (typeof value === "number") {
      return `${value.toFixed(2)}%`;
    }
    return value || "---";
  };

  // Get selected account info
  const selectedAccountObj =
    accounts.find((acc) => acc.id === selectedAccount) || {};

  const sortedPortfolioPlayTrades = React.useMemo(() => {
    if (!isPortfolioPlayMode || !playSortConfig.key || !Array.isArray(userEditedTrades)) {
      return userEditedTrades;
    }

    const getRatingValue = (rating, isSnp = true) => {
      if (!rating) return Number.MAX_SAFE_INTEGER;
      const ratingOptions = isSnp ? snpRatingOptions : moodyRatingOptions;
      const ratingObj = ratingOptions.find((r) => r.rating === rating);
      return ratingObj ? ratingObj.value : Number.MAX_SAFE_INTEGER;
    };

    return [...userEditedTrades].sort((a, b) => {
      const aValue = a[playSortConfig.key];
      const bValue = b[playSortConfig.key];

      if (playSortConfig.key === "spRating" || playSortConfig.key === "moodyRating") {
        const isSnp = playSortConfig.key === "spRating";
        const aRatingValue = getRatingValue(aValue, isSnp);
        const bRatingValue = getRatingValue(bValue, isSnp);

        if (aRatingValue === Number.MAX_SAFE_INTEGER && bRatingValue === Number.MAX_SAFE_INTEGER) {
          return 0;
        }
        if (aRatingValue === Number.MAX_SAFE_INTEGER) return 1;
        if (bRatingValue === Number.MAX_SAFE_INTEGER) return -1;

        return playSortConfig.direction === "asc"
          ? bRatingValue - aRatingValue
          : aRatingValue - bRatingValue;
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const aNum = Number(aValue);
      const bNum = Number(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return playSortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (playSortConfig.direction === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      }
      return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
    });
  }, [
    isPortfolioPlayMode,
    userEditedTrades,
    playSortConfig,
    snpRatingOptions,
    moodyRatingOptions,
  ]);

  const handlePortfolioPlaySort = (columnKey, direction) => {
    setPlaySortConfig({ key: columnKey, direction });
  };

  // Handle CUSIP click
  const handleCusipClick = async (index, rowData) => {
    setBondDetailsLoading(true);
    setBondDetailsOpen(true);
    try {
      const details = await fetchSecurityInfo(token, rowData.cusip);
      setSelectedBondDetails(details);
    } catch (e) {
      setSelectedBondDetails(null);
    } finally {
      setBondDetailsLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Always return to the workbench (with its existing data), NOT the arbitrary
    // page the user came from (e.g. when opened via the notification from another
    // page, navigate(-1) would wrongly go back there).
    navigate("/portfolio-rebalance");
  };

  // Handle submit button click (open confirmation modal)
  const handleSubmit = () => {
    setOrderError("");
    if (eligibleTradesForSubmit.length === 0) {
      return;
    }
    // If userEditedTrades and tradesRecalculated differ, show informative modal
    if (!isPortfolioPlayMode && isRecalculateButtonEnabled()) {
      setShowInfoModal(true);
      return;
    }
    setIsConfirmModalOpen(true);
  };

  // Handler for confirming informative modal (Ok)
  const handleInfoModalConfirm = () => {
    setShowInfoModal(false);
    // Trigger Undo: reset userEditedTrades to recommendedTrades
    handleUndoTradeQtys();
    // Proceed with submission (using recommendedTrades)
    setIsConfirmModalOpen(true);
  };

  // Handler for cancelling informative modal (Cancel)
  const handleInfoModalCancel = () => {
    setShowInfoModal(false);
    // User can now recalculate if desired
  };

  // Execute order submission after confirmation
  const executeSubmitOrders = async () => {
    if (!token) return;
    setIsOrderProcessing(true);
    setOrderError("");
    try {
      // Prepare bulk order payload using userEditedTrades where includeInRebalancing is true
      const ordersToSubmit = eligibleTradesForSubmit
        .map((order) => ({
          account: selectedAccountObj.accountIdentifier,
          qty: Math.abs(order.tradeQuantity), // Ensure qty is positive
          cusip: order.cusip,
          price: order.currentPrice,
          ...stagingOrderFields(),
          side: order.side || order.action,
          characteristicId: null,
          bondLadderId: null,
          // pmId: selectedAccountObj.pmId || null,
          userId: userId || null,
          // pm: selectedAccountObj.pmName || "",
          userName: selectedAccountObj.userName || "",
          comments: order.comments || "",
          bondInventoryId: order.bondInventoryId || null,
        }));

      // Submit bulk orders
      await placeBulkOrdersPortfolioRebalance(token, ordersToSubmit);

      setIsOrderSuccess(true);
      setIsSuccessModalOpen(true);
      setIsConfirmModalOpen(false);
      setIsOrderProcessing(false);

      // Do not reset here; we'll reset after navigating on success modal close to avoid redirecting back
    } catch (error) {
      setOrderError(error.message || "Failed to submit rebalance order");
      setIsOrderSuccess(false);
      setIsSuccessModalOpen(true);
      setIsConfirmModalOpen(false);
      setIsOrderProcessing(false);
    }
  };

  // Handle cancel confirmation modal
  const handleCancelConfirmation = () => {
    if (!isOrderProcessing) {
      setIsConfirmModalOpen(false);
      setOrderError("");
    }
  };

  // Handle success modal close (navigate to staging blotter if success)
  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    if (isOrderSuccess) {
      navigate("/order");
      // Reset store after navigating so the preview screen's redirect effect doesn't bounce back
      usePortfolioRebalanceStore.getState().reset();
    }
    // For error, stay on page
  };

  // Redirect if no API data
  useEffect(() => {
    if (
      !isPortfolioPlayMode &&
      !rebalanceApiData &&
      !isOrderProcessing &&
      !isOrderSuccess &&
      !isSuccessModalOpen
    ) {
      navigate("/portfolio-rebalance");
    }
  }, [rebalanceApiData, navigate, isOrderProcessing, isOrderSuccess, isSuccessModalOpen, isPortfolioPlayMode]);

  // Show error if API error
  useEffect(() => {
    if (rebalanceApiError) {
      showError(rebalanceApiError);
    }
  }, [rebalanceApiError, showError]);

  // On first load or when trades change, initialize trade states
  useEffect(() => {
    if (isPortfolioPlayMode) {
      if (!initialPortfolioPlayTrades.length) {
        navigate("/portfolio-rebalance");
        return;
      }

      const trades = initialPortfolioPlayTrades.map((trade) => ({
        ...trade,
        side: trade.includeInRebalancing ? (trade.side || trade.action || "BUY") : (trade.side || trade.action),
        action: trade.includeInRebalancing ? (trade.action || trade.side || "BUY") : (trade.action || trade.side),
      }));
      setRecommendedTrades(trades);
      setUserEditedTrades(JSON.parse(JSON.stringify(trades)));
      setTradesRecalculated(JSON.parse(JSON.stringify(trades)));
      return;
    }

    if (rebalanceApiData?.originalPlusUpdatedTrades) {
      const trades = rebalanceApiData.originalPlusUpdatedTrades;
      setRecommendedTrades(trades);
      setUserEditedTrades(JSON.parse(JSON.stringify(trades))); // Deep copy
      setTradesRecalculated(JSON.parse(JSON.stringify(trades))); // Deep copy
    }
  }, [
    isPortfolioPlayMode,
    initialPortfolioPlayTrades,
    rebalanceApiData?.originalPlusUpdatedTrades,
    navigate,
  ]);

  // Dedicated function to check if Undo button should be enabled
  const isUndoButtonEnabled = () => {
    if (recommendedTrades.length !== userEditedTrades.length) return true;
    return userEditedTrades.some((trade, i) => {
      return (
        trade.tradeQuantity !== recommendedTrades[i].tradeQuantity ||
        trade.endingSecurityWeightPercentage !== recommendedTrades[i].endingSecurityWeightPercentage
      );
    });
  };

  // Dedicated function to check if Recalculate button should be enabled
  const isRecalculateButtonEnabled = () => {
    if (userEditedTrades.length !== tradesRecalculated.length) return true;
    return userEditedTrades.some((trade, i) => {
      return trade.tradeQuantity !== tradesRecalculated[i].tradeQuantity;
    });
  };

  // Recalculate handler: call rebalance API with updated trade quantities
  const handleRecalculate = async (tradesOverride = null, editMeta = null) => {
    const rebalanceType = portfolioSummary?.rebalance || location.state?.rebalanceType;
    if (!rebalanceType) {
      showError("Rebalance type is missing. Please go back and select a rebalance type.");
      return;
    }
    setIsRecalculateLoading(true);

    try {
      const tradesToUse = Array.isArray(tradesOverride) ? tradesOverride : userEditedTrades;
      const totalEndingSecurityMarketValue = tradesToUse.reduce(
        (sum, trade) => sum + (trade.endingSecurityMarketValue || trade.endingSecurityMarketValue || 0),
        0
      );
      // Prepare bonds payload using userEditedTrades
      const bonds = tradesToUse.map((trade) => ({
        side: trade.includeInRebalancing ? trade.side || trade.action : null,
        cusip: trade.cusip,
        securityMarketValue: trade.securityMarketValue,
        currentQuantity: trade.includeInRebalancing ? trade.currentQuantity : trade.quantity,
        price: trade.price ?? trade.currentPrice,
        accruedInterest: trade.accruedInterest || 0,
        endingQuantity: trade.endingQuantity,
        // endingQuanty: trade.endingQuantity,
        endingSecurityMarketValue:
          trade.endingSecurityMarketValue ?? trade.endingSecurityMarketValue ?? 0,
        yield: trade.yield,
        duration: trade.duration,
        dv01: trade.dv01,
        ratingSnp: trade.ratingSnp ?? trade.spRating,
        ratingMoody: trade.ratingMoody ?? trade.moodyRating,
        tradeQuantity: trade.tradeQuantity,
        tradeValue: trade.tradeValue,
        includeInRebalancing: trade.includeInRebalancing,
      }));

      // Determine rebalanceVariable
      let rebalanceVariable;
      switch (rebalanceType) {
        case "Duration":
          rebalanceVariable = "duration";
          break;
        case "Yield":
          rebalanceVariable = "yield";
          break;
        case "S&P Rating":
          rebalanceVariable = "sprating";
          break;
        case "Moody's Rating":
          rebalanceVariable = "ratingmoody";
          break;
        case "DV01":
          rebalanceVariable = "dv01";
          break;
        default:
          throw new Error("Invalid rebalance type selected");
      }

      // Build request body
      const requestBody = {
        rebalanceVariable,
        bonds,
        totalEndingSecurityMarketValue,
        editedCusip: editMeta?.editedCusip ?? null,
        editedEndingSecurityWeightPercentage:
          editMeta?.editedEndingSecurityWeightPercentage ?? null,
      };

      // Call the rebalance API
      const token = localStorage.getItem("token");
      const { fetchPortfolioRecalculate } = await import("../http/api");
      const responseData = await fetchPortfolioRecalculate(token, requestBody);

      // Handle 400 error
      if (responseData && responseData.status === 400) {
        showError("Something went wrong. Please try again.");
        setIsRecalculateLoading(false);
        return;
      }

      // Update achieved value/rating and total ending market value on screen
      usePortfolioRebalanceStore.setState((state) => ({
        rebalanceApiData: {
          ...(state.rebalanceApiData || {}),
          targetValueAchieved: responseData.targetValueAchieved,
          targetRatingAchieved: responseData.targetRatingAchieved,
          summary: {
            ...(state.rebalanceApiData?.summary || {}),
            totalEndingMarketValue: responseData.totalEndingMarketValue,
          },
        },
      }));

      // Update tradesRecalculated and userEditedTrades with endingQuantity from API response
      const updateTradesWithResponseData = (prevTrades, responseTrades) => {
        return prevTrades.map((trade, idx) => {
          const responseTrade = responseTrades[idx] || {};
          return {
            ...trade,
            ...Object.keys(responseTrade).reduce((acc, key) => {
              if (responseTrade[key] !== null) {
                acc[key] = responseTrade[key];
              }
              return acc;
            }, {}),
          };
        });
      };

      if (Array.isArray(responseData.trades)) {
        setUserEditedTrades(updateTradesWithResponseData(tradesToUse, responseData.trades));
        setTradesRecalculated(tradesToUse);
      }

      setIsRecalculateLoading(false);
    } catch (error) {
      showError(error.message || "Failed to recalculate");
      setIsRecalculateLoading(false);
    }
  };

  const handlePortfolioPlayRecalculate = async (tradesOverride = null, editMeta = null) => {
    setIsRecalculateLoading(true);

    try {
      const tradesToUse = Array.isArray(tradesOverride) ? tradesOverride : userEditedTrades;
      const totalEndingSecurityMarketValue = tradesToUse.reduce(
        (sum, trade) => sum + (trade.endingSecurityMarketValue || 0),
        0
      );

      const bonds = tradesToUse.map((trade) => ({
        side: trade.includeInRebalancing ? trade.side || trade.action : null,
        cusip: trade.cusip,
        securityMarketValue: trade.securityMarketValue,
        currentQuantity: trade.includeInRebalancing ? trade.currentQuantity : trade.quantity,
        price: trade.price ?? trade.currentPrice,
        accruedInterest: trade.accruedInterest || 0,
        endingQuantity: trade.endingQuantity,
        endingSecurityMarketValue: trade.endingSecurityMarketValue ?? 0,
        yield: trade.yield,
        duration: trade.duration,
        dv01: trade.dv01,
        ratingSnp: trade.ratingSnp ?? trade.spRating,
        ratingMoody: trade.ratingMoody ?? trade.moodyRating,
        tradeQuantity: trade.tradeQuantity,
        tradeValue: trade.tradeValue,
        includeInRebalancing: trade.includeInRebalancing,
      }));

      const requestBody = {
        bonds,
        totalEndingSecurityMarketValue,
        editedCusip: editMeta?.editedCusip ?? null,
        editedEndingSecurityWeightPercentage:
          editMeta?.editedEndingSecurityWeightPercentage ?? null,
      };

      const token = localStorage.getItem("token");
      const { fetchPortfolioPlayRecalculate } = await import("../http/api");
      const responseData = await fetchPortfolioPlayRecalculate(token, requestBody);

      if (responseData && responseData.status === 400) {
        showError("Something went wrong. Please try again.");
        setIsRecalculateLoading(false);
        return;
      }

      usePortfolioRebalanceStore.setState((state) => ({
        rebalanceApiData: {
          ...(state.rebalanceApiData || {}),
          targetValueAchieved: responseData.targetValueAchieved,
          targetRatingAchieved: responseData.targetRatingAchieved,
          summary: {
            ...(state.rebalanceApiData?.summary || {}),
            totalEndingMarketValue:
              responseData.totalEndingMarketValue ?? responseData.totalEndingSecurityMarketValue,
          },
        },
      }));

      const updateTradesWithResponseData = (prevTrades, responseTrades) => {
        return prevTrades.map((trade, idx) => {
          const responseTrade = responseTrades[idx] || {};
          return {
            ...trade,
            ...Object.keys(responseTrade).reduce((acc, key) => {
              if (responseTrade[key] !== null) {
                acc[key] = responseTrade[key];
              }
              return acc;
            }, {}),
          };
        });
      };

      if (Array.isArray(responseData.trades)) {
        setUserEditedTrades(updateTradesWithResponseData(tradesToUse, responseData.trades));
        setTradesRecalculated(tradesToUse);
      }

      setIsRecalculateLoading(false);
    } catch (error) {
      showError(error.message || "Failed to recalculate");
      setIsRecalculateLoading(false);
    }
  };

  const isRecalculateEnabled = !isPortfolioPlayMode && isRecalculateButtonEnabled();
  const isSubmitDisabled = isRecalculateEnabled || eligibleTradesForSubmit.length === 0;

  // Reusable metric component
  const MetricItem = ({ label, value, showSeparator = true }) => (
    <>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          mr: "4px",
          backgroundColor: tokens.colors.bodyBorderColor50,
          height: "40px",
          alignSelf: "center",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          mr: showSeparator ? 2 : 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: tokens.colors.fontColour85, fontSize: "14px", mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: tokens.colors.fontColour, fontWeight: 400, fontSize: "14px" }}
        >
          {value}
        </Typography>
      </Box>
    </>
  );

  // Helper function to get summary or account value
  const getSummaryOrAccount = (summaryValue, accountValue) => {
    if (
      summaryValue === null ||
      summaryValue === undefined ||
      summaryValue === ""
    ) {
      return accountValue || "---";
    }
    return summaryValue;
  };

  // Preview metrics data
  const orderPreviewMetrics = [
    {
      label: "Buy Trade",
      value: previewMetrics.buyTradeValue,
    },
    {
      label: "Sell Trade",
      value: previewMetrics.sellTradeValue,
    },
    {
      label: "Buy Trade Amt",
      value: formatCurrency(previewMetrics.buyTradeAmt),
    },
    {
      label: "Sell Trade Amt",
      value: formatCurrency(previewMetrics.sellTradeAmt),
    },
    {
      label: "Total Trade Amount",
      value: formatCurrency(previewMetrics.netTradeValue),
    },
    {
      label: "Total Ending Security Market Value",
      value: formatCurrency(previewMetrics.totalEndingSecurityMarketValue),
    },
    {
      label: "Turnover",
      value: formatPercentage(previewMetrics.turnover),
      showSeparator: false, // Only last metric disables separator
    },
  ];

  // Get target value achieved from API response
  const targetValueAchieved = rebalanceApiData?.targetValueAchieved;
  const targetRatingAchieved = rebalanceApiData?.targetRatingAchieved;

  // Utility function to convert years to months


  // Get original value from accountSummary based on rebalance type
  const getOriginalValue = () => {
    if (!portfolioSummary.rebalance) return "---";
    switch (portfolioSummary.rebalance) {
      case "Duration":
        return accountSummary.average !== undefined &&
          accountSummary.average !== null
          ? (parseFloat(accountSummary.average))
          : "---";
      case "Yield":
        return accountSummary.averageYieldToWorst !== undefined &&
          accountSummary.averageYieldToWorst !== null
          ? parseFloat(accountSummary.averageYieldToWorst).toFixed(2)
          : "---";
      case "DV01":
        return accountSummary.totalDv01 !== undefined &&
          accountSummary.totalDv01 !== null
          ? parseFloat(accountSummary.totalDv01).toFixed(2)
          : "---";
      case "S&P Rating":
        return accountSummary.averageSPRating || "---";
      case "Moody's Rating":
        return accountSummary.averageMoodys || "---";
      default:
        return "---";
    }
  };

  // Determine which target value to display based on rebalance type
  const getTargetDisplay = () => {
    if (!portfolioSummary.rebalance) return null;

    let targetAchieved = "";
    let originalValue = getOriginalValue();

    if (portfolioSummary.rebalance === "Duration") {
      targetAchieved =
        targetValueAchieved != null
          ? parseFloat(targetValueAchieved).toFixed(2)
          : "---";
    } else if (
      ["S&P Rating", "Moody's Rating"].includes(portfolioSummary.rebalance)
    ) {
      targetAchieved = targetRatingAchieved || "---";
    } else {
      targetAchieved = targetValueAchieved
        ? parseFloat(targetValueAchieved).toFixed(2)
        : "---";
    }

    return {
      type:
        portfolioSummary.rebalance === "Duration"
          ? "Duration"
          : portfolioSummary.rebalance,
      original: originalValue,
      achieved: targetAchieved,
    };
  };

  const targetDisplay = getTargetDisplay();

  // Extract minimum trade quantity from available sources (API response or portfolio summary)
  const minTradeQty =
    rebalanceApiData?.minTradeQty ??
    rebalanceApiData?.summary?.minTradeQty ??
    portfolioSummary?.minTradeQty ??
    null;

  // Extract and normalize lot size from available sources (API or portfolio summary)
  const _rawLotSize =
    rebalanceApiData?.lotSize ??
    rebalanceApiData?.summary?.lotSize ??
    portfolioSummary?.lotSize ??
    null;
  const lotSize = _rawLotSize != null ? String(_rawLotSize).trim() : null;

  const hasDisplayValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "" &&
    String(value).trim() !== "---";

  const accountIdentifierDisplay = getSummaryOrAccount(
    accountSummary.accountIdentifier,
    selectedAccountObj.accountIdentifier
  );
  const accountDescriptionDisplay = getSummaryOrAccount(
    accountSummary.accountDescription,
    selectedAccountObj.description
  );

  const headerInfoSegments = [];

  if (hasDisplayValue(accountIdentifierDisplay)) {
    headerInfoSegments.push(
      <>
        <span>Account:</span> <span style={{ fontWeight: 700 }}>{accountIdentifierDisplay}</span>
      </>
    );
  }

  if (hasDisplayValue(accountDescriptionDisplay)) {
    headerInfoSegments.push(
      <>
        <span>Account Description:</span> <span style={{ fontWeight: 700 }}>{accountDescriptionDisplay}</span>
      </>
    );
  }

  if (!isPortfolioPlayMode && targetDisplay) {
    if (minTradeQty != null) {
      headerInfoSegments.push(
        <>
          <span>Min Trade Qty:</span>{" "}
          <span style={{ fontWeight: 700 }}>
            {Number(minTradeQty).toLocaleString("en-US")}
          </span>
        </>
      );
    }

    if (lotSize != null && lotSize !== "1") {
      headerInfoSegments.push(
        <>
          <span>Lot Size:</span> <span style={{ fontWeight: 700 }}>{String(lotSize)}</span>
        </>
      );
    }

    if (hasDisplayValue(portfolioSummary.targetValue)) {
      headerInfoSegments.push(
        <>
          <span>Target:</span> <span style={{ fontWeight: 700 }}>{portfolioSummary.targetValue}</span>
        </>
      );
    }

    if (hasDisplayValue(portfolioSummary.toleranceValue)) {
      headerInfoSegments.push(
        <>
          <span>Tolerance:</span> <span style={{ fontWeight: 700 }}>{portfolioSummary.toleranceValue}</span>
        </>
      );
    }

    if (
      hasDisplayValue(targetDisplay.type) &&
      hasDisplayValue(targetDisplay.original) &&
      hasDisplayValue(targetDisplay.achieved)
    ) {
      headerInfoSegments.push(
        <>
          <span>Rebalance:</span>{" "}
          <span
            style={{
              color: tokens.palette.primary,
              fontWeight: 700,
            }}
          >
            {targetDisplay.type} {targetDisplay.original} to {targetDisplay.achieved}
          </span>
        </>
      );
    }
  }

  // Helper to find a trade index by stable identity
  const findTradeIndexByRow = (list, row) => {
    if (!Array.isArray(list) || !row) return -1;
    // Prefer accountPositionMappingId if present
    if (row.accountPositionMappingId != null) {
      const idx = list.findIndex(
        (t) => t.accountPositionMappingId === row.accountPositionMappingId
      );
      if (idx !== -1) return idx;
    }
    // Fallback to cusip + side/action
    const side = row.side || row.action || null;
    if (row.cusip && side) {
      const idx = list.findIndex(
        (t) => t.cusip === row.cusip && (t.side || t.action) === side
      );
      if (idx !== -1) return idx;
    }
    // match by cusip only
    if (row.cusip) {
      return list.findIndex((t) => t.cusip === row.cusip);
    }
    // Last resort: Fallback to generic id
    if (row.id != null) {
      const idx = list.findIndex((t) => t.id === row.id);
      if (idx !== -1) return idx;
    }
    return -1;
  };

  // Handle trade qty change in preview table (identify row by id instead of index)
  const handleTradeQtyChange = (rowIndex, rowData, newValue) => {
    if (newValue === "" || (typeof newValue === "number" && newValue >= 0)) {
      setUserEditedTrades((prev) => {
        const updated = [...prev];
        const idx = findTradeIndexByRow(updated, rowData);
        if (idx !== -1) {
          // Use helper function to automatically calculate ending qty
          const updatedTrade = updateTradeRowCalculations(
            updated[idx],
            "tradeQuantity",
            newValue === "" ? "" : Number(newValue)
          );
          updated[idx] = updatedTrade;
        }
        return updated;
      });
    }
  };

  // Show EditQty modal for a row (no index dependence)
  const handleEditQtyClick = (rowData) => {
    setEditQtyRowData(rowData);
    setEditQtyOpen(true);
  };

  // Show Edit Ending Weight modal
  const handleEditEndingWeightClick = (rowData) => {
    setEditEndingWeightRowData(rowData);
    setEditEndingWeightOpen(true);
  };

  // Submit new values from modal (now receives object with all 4 values and potentially flipped side)
  const handleEditQtySubmit = (submittedValues) => {
    let updatedTrades = null;
    setUserEditedTrades((prev) => {
      const updated = [...prev];
      const idx = findTradeIndexByRow(updated, editQtyRowData);
      if (idx !== -1) {
        // Update with all submitted values including side if it was flipped
        updated[idx] = {
          ...updated[idx],
          tradeQuantity: submittedValues.tradeQuantity,
          endingQuantity: submittedValues.endingQuantity,
          tradeValue: submittedValues.tradeValue,
          endingSecurityMarketValue: submittedValues.endingSecurityMarketValue,
          ...(submittedValues.side && { side: submittedValues.side }), // Update side if provided
        };
      }
      updatedTrades = updated;
      return updated;
    });

    // Auto-trigger recalculate after successful submit using the updated trades
    // Skip recalculate API in portfolio play mode
    if (!isPortfolioPlayMode) {
      setTimeout(() => {
        handleRecalculate(updatedTrades, {
          editedCusip: editQtyRowData?.cusip ?? null,
          editedEndingSecurityWeightPercentage: null,
        });
      }, 0);
    }
  };

  // Submit new ending weight percentage
  const handleEditEndingWeightSubmit = (endingWeightValue) => {
    let updatedTrades = null;
    setUserEditedTrades((prev) => {
      const updated = [...prev];
      const idx = findTradeIndexByRow(updated, editEndingWeightRowData);
      if (idx !== -1) {
        updated[idx] = {
          ...updated[idx],
          endingSecurityWeightPercentage: endingWeightValue,
        };
      }
      updatedTrades = updated;
      return updated;
    });

    // Auto-trigger recalculate after successful submit using the updated trades
    setTimeout(() => {
      const editMeta = {
        editedCusip: editEndingWeightRowData?.cusip ?? null,
        editedEndingSecurityWeightPercentage:
          endingWeightValue ?? null,
      };

      if (isPortfolioPlayMode) {
        handlePortfolioPlayRecalculate(updatedTrades, editMeta);
        return;
      }

      handleRecalculate(updatedTrades, editMeta);
    }, 0);
  };

  const getRecalculatedRowForSide = (trade, side) => {
    if (!trade) return null;
    const normalizedSide = String(side || "").toUpperCase();
    const normalizedQty = Math.abs(Number(trade.tradeQuantity) || 0);
    const recalculated = updateTradeRowCalculations(
      {
        ...trade,
        side: normalizedSide,
        action: normalizedSide,
      },
      "tradeQuantity",
      normalizedQty
    );
    return {
      ...recalculated,
      side: normalizedSide,
      action: normalizedSide,
    };
  };

  const isSideValidForRow = (trade, side) => {
    const recalculated = getRecalculatedRowForSide(trade, side);
    if (!recalculated) return false;

    const validation = validateCalculatedRow(recalculated);
    if (!validation.isValid) return false;

    const currentQty = Number(trade?.currentQuantity) || 0;
    const tradeQty = Number(recalculated.tradeQuantity) || 0;
    if (String(side || "").toUpperCase() === "SELL" && tradeQty > currentQty) {
      return false;
    }

    return true;
  };

  // Undo handler: reset userEditedTrades to recommendedTrades
  const handleUndoTradeQtys = () => {
    const resetTrades = JSON.parse(JSON.stringify(recommendedTrades));
    setUserEditedTrades(resetTrades);
    setTradesRecalculated(resetTrades);
    //also update the achieved value to what was originally calculated
    usePortfolioRebalanceStore.setState((state) => ({
      rebalanceApiData: {
        ...state.rebalanceApiData,
        targetValueAchieved: state.rebalanceApiData.beforeEditedAchievedTargetValue,
        targetRatingAchieved: state.rebalanceApiData.beforeEditedAchievedTargetRating,
      },
    }));
  };

  // Helper function to create cell renderers for editable fields
  const createCellRenderer = (fieldConfig, onClick) => (value, row) => {
    // In portfolio-play mode all rows are editable so the user can directly manipulate
    // any position regardless of the includeInRebalancing flag (which starts as false).
    // In normal order-preview mode, only rows flagged includeInRebalancing can be edited.
    const isEditable = isPortfolioPlayMode || row?.includeInRebalancing === true;
    const displayValue = fieldConfig.formatValue(row[fieldConfig.key]);

    if (!isEditable) {
      return (
        <span style={{ color: tokens.colors.fontColour }}>
          {displayValue}
        </span>
      );
    }

    return (
      <span
        style={{
          cursor: "pointer",
          textDecoration: "underline",
          color: tokens.colors.primary,
          padding: "2px 4px",
          borderRadius: 2,
          fontWeight: 700,
        }}
        onClick={() => onClick(row)}
      >
        {displayValue}
      </span>
    );
  };

  // Generate all cell renderers from config
  const tradeQtyCellRenderer = createCellRenderer(EDITABLE_FIELDS[0], handleEditQtyClick);
  const endingQtyCellRenderer = createCellRenderer(EDITABLE_FIELDS[1], handleEditQtyClick);
  const tradeValueCellRenderer = createCellRenderer(EDITABLE_FIELDS[2], handleEditQtyClick);
  const endingSecurityMarketValueCellRenderer = createCellRenderer(EDITABLE_FIELDS[3], handleEditQtyClick);
  const endingWeightCellRenderer = createCellRenderer(EDITABLE_FIELDS[4], handleEditEndingWeightClick);

  return (
    <>
      <SnackbarComponent />

      {/* Loading overlay */}
      <Backdrop
        sx={{
          color: tokens.colors.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: withAlpha('#000000', 0.7),
          flexDirection: "column",
          gap: 2,
        }}
        open={rebalanceApiLoading || isRecalculateLoading}
      >
        <CircularProgress sx={{ color: tokens.colors.white }} size={60} />
        <Typography variant="h6" sx={{ color: tokens.colors.white }}>
          Recalculating...
        </Typography>
      </Backdrop>

      <Box
        sx={{
          height: "calc(100vh - 84px)",
          backgroundColor: tokens.colors.bodyBackgroundColor,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 0,
            "&.MuiContainer-root": {
              paddingLeft: 0,
              paddingRight: 0,
            },
          }}
        >
          {/* Header */}
          <Box sx={{ flexShrink: 0 }}>
            <Paper
              sx={{
                backgroundColor: tokens.colors.defaultGridRowColor,
                borderRadius: 0,
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                mb: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                  pt: 2,
                  mb: 1,
                  minHeight: "40px",
                }}
              >
                <Box sx={{ pl: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: tokens.colors.fontColour,
                      fontWeight: 700,
                      fontSize: "20px",
                    }}
                  >
                    Preview
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pr: "10px",
                    pt: "2px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: tokens.colors.fontColour85,
                      fontWeight: 700,
                      fontSize: "14px",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {headerInfoSegments.map((segment, index) => (
                      <React.Fragment key={`header-info-${index}`}>
                        {index > 0 && <>&nbsp; | &nbsp;</>}
                        {segment}
                      </React.Fragment>
                    ))}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2, pt: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      borderRadius: "2px",
                      bgcolor: tokens.colors.bodyBackgroundColor,
                      p: "4px",
                      pl: "10px",
                      pr: 2,
                    }}
                  >
                    {orderPreviewMetrics.map((metric, index) => (
                      <MetricItem
                        key={index}
                        label={metric.label}
                        value={metric.value}
                        showSeparator={metric.showSeparator !== false}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {!isPortfolioPlayMode && (
                      <Button
                        variant="outlined"
                        onClick={handleUndoTradeQtys}
                        disabled={!isUndoButtonEnabled()}
                        sx={{
                          textTransform: "none",
                          fontWeight: "700",
                          px: 2,
                          py: 1,
                          width: "150px",
                          height: "40px",
                          borderRadius: "8px",
                          mr: 2,
                        }}
                      >
                        Undo
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={handleRecalculate}
                      disabled={!isRecalculateButtonEnabled() || isRecalculateLoading}
                      sx={{
                        display: "none",
                        textTransform: "none",
                        fontWeight: "700",
                        px: 2,
                        py: 1,
                        width: "150px",
                        height: "40px",
                        borderRadius: "8px",
                        color: tokens.colors.buyFontColor,
                        borderColor: tokens.colors.buyBorderColor,
                        backgroundColor: withAlpha(tokens.colors.buyBgColor, 0.08),
                        "&:hover": {
                          borderColor: tokens.colors.buyBorderColor,
                          backgroundColor: withAlpha(tokens.colors.buyBorderColor, 0.12),
                        },
                        mr: 2,
                      }}
                    >
                      {isRecalculateLoading ? "Recalculating..." : "Recalculate"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Table with preview data */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              mt: 0,
            }}
          >
            {isPortfolioPlayMode ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "calc(100vh - 297px)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: tokens.fontColour,
                      fontWeight: 600,
                      fontSize: "16px",
                    }}
                  >
                    Portfolio Play Trades ({userEditedTrades ? userEditedTrades.length.toLocaleString() : 0})
                  </Typography>
                </Box>

                <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                  <BondTable
                    selectedColumns={PORTFOLIO_PLAY_COLUMNS}
                    filteredData={sortedPortfolioPlayTrades}
                    handleRowClick={() => { }}
                    handleViewJson={() => { }}
                    sectionKey="portfolio-play"
                    source="portfolio-play"
                    handleCusipClick={handleCusipClick}
                    loading={false}
                    onTradeQtyChange={handleTradeQtyChange}
                    tradeQtyCellRenderer={tradeQtyCellRenderer}
                    endingQtyCellRenderer={endingQtyCellRenderer}
                    tradeValueCellRenderer={tradeValueCellRenderer}
                    endingSecurityMarketValueCellRenderer={endingSecurityMarketValueCellRenderer}
                    endingWeightCellRenderer={endingWeightCellRenderer}
                    showCheckboxes={false}
                    onSort={handlePortfolioPlaySort}
                    sortConfig={playSortConfig}
                    allowEditWithoutSide
                  />
                </Box>
              </Box>
            ) : (
              <OrderPreviewTable
                data={userEditedTrades}
                onCusipClick={handleCusipClick}
                rebalanceFactor={portfolioSummary.rebalance}
                onTradeQtyChange={handleTradeQtyChange}
                tradeQtyCellRenderer={tradeQtyCellRenderer}
                endingQtyCellRenderer={endingQtyCellRenderer}
                tradeValueCellRenderer={tradeValueCellRenderer}
                endingSecurityMarketValueCellRenderer={endingSecurityMarketValueCellRenderer}
                endingWeightCellRenderer={endingWeightCellRenderer}
                isPortfolioPlayMode={isPortfolioPlayMode}
              />
            )}
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              p: 2,
              borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
              backgroundColor: tokens.colors.defaultGridRowColor,
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 2,
                py: 1,
                width: "150px",
                height: "40px",
                borderRadius: "8px",
                color: tokens.colors.fontColour,
                borderColor: tokens.colors.bodyBorderColor50,
                "&:hover": {
                  borderColor: tokens.colors.bodyBorderColor50,
                  backgroundColor: tokens.colors.defaultGridHoverColor,
                },
              }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 2,
                py: 1,
                width: "150px",
                height: "40px",
                borderRadius: "8px",
                backgroundColor: tokens.palette.primary,
                color: tokens.colors.white,
                opacity: isSubmitDisabled ? 0.6 : 1,
                cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                "&:hover": {
                  backgroundColor: tokens.palette.primaryA80,
                },
              }}
            >
              Submit
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Bond Details Modal */}
      <BondDetailsModal
        open={bondDetailsOpen}
        onClose={() => setBondDetailsOpen(false)}
        bondDetails={selectedBondDetails}
        loading={bondDetailsLoading}
      />

      {/* Informative Modal for submitting without recalculation */}
      <InformativeModal
        open={!isPortfolioPlayMode && showInfoModal}
        onConfirm={handleInfoModalConfirm}
        onClose={handleInfoModalCancel}
        showCancel={true}
        title="Warning"
        message="You have changed trade quantities but have not recalculated. Submission will be done using the original trade quantities. Do you want to continue?"
      />

      {/* Confirmation Modal */}
      <OrderConfirmationModal
        isConfirmOpen={isConfirmModalOpen}
        isLoading={isOrderProcessing}
        orderCount={eligibleTradesForSubmit.length}
        onConfirm={executeSubmitOrders}
        onCancel={handleCancelConfirmation}
        errorMessage={orderError}
        isRebalance={true}
      />

      {/* Success Modal */}
      <OrderSuccessModal
        open={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        success={isOrderSuccess}
        errorMessage={orderError}
        orderCount={eligibleTradesForSubmit.length}
      />

      {/* Edit Qty Modal */}
      <EditQtyPortfolioRebalance
        open={editQtyOpen}
        onClose={() => setEditQtyOpen(false)}
        rowData={editQtyRowData}
        originalQty={(function () {
          if (!editQtyRowData) return "";
          const idx = findTradeIndexByRow(recommendedTrades, editQtyRowData);
          return idx !== -1
            ? recommendedTrades[idx]?.tradeQuantity ?? ""
            : "";
        })()}
        onSubmit={handleEditQtySubmit}
        isPortfolioPlayMode={isPortfolioPlayMode}
      />

      <EditEndingWeightPortfolioRebalance
        open={editEndingWeightOpen}
        onClose={() => setEditEndingWeightOpen(false)}
        rowData={editEndingWeightRowData}
        onSubmit={handleEditEndingWeightSubmit}
      />
    </>
  );
};

export default OrderPreviewScreen;
